import '.a{display:grid;}.ssr-plugin'
import '.a8{grid-template-columns:1fr 1fr;}.ssr-plugin'
import { PageLayout } from '../layout/PageLayout'
import heroAboutUsImage from '@app/assets/images/for-municipalities/hero.png'
import saschaImage from '@app/assets/images/about-us/team/Sascha.jpg'
import freddieImage from '@app/assets/images/about-us/team/Freddie.jpg'
import transitionImage from '@app/assets/images/for-municipalities/transition.jpg'
import ideaBulbImage from '@app/assets/images/for-municipalities/idea-bulb.svg'
import creditCardImage from '@app/assets/images/for-municipalities/credit-card.svg'
import magnifyGlassImage from '@app/assets/images/for-municipalities/magnify-glass.svg'
import uniqueEnvironmentImage from '@app/assets/images/for-municipalities/unique-online-environment.png'
import knowledgePartnerImage from '@app/assets/images/for-municipalities/knowledge-partner.svg'
import starImage from '@app/assets/images/for-municipalities/star.svg'
import { Style, styled } from 'inlines'
import { ColumnsSection } from '@app/components/blocks/contentSection/ColumnsSection'
import { OurPartners } from './blocks/OurPartners'
import { Title } from '@app/components/core/typography/Title'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import { useMemo } from 'react'
import { Text } from '@app/components/core/typography/Text'
import { Card } from '@app/components/blocks/card/Card'
import Column from '@app/components/layout/Column'
import { TeamCard } from '@app/components/blocks/card/TeamCard'
import { Hero } from '@app/components/blocks/hero/Hero'
import { pages } from '@app/util/links'
import { Paragraph } from '@app/components/core/typography/Paragraph'

export const ForMunicipalities = () => {
  const isMobile = useMediaQuery()

  const DemoTextComponent = isMobile ? Text.Medium : Text.Large

  const imageStyle: Style = useMemo(
    () => ({
      height: isMobile ? 'auto' : '31rem',
      width: isMobile ? '100%' : '31rem',
      aspectRatio: '1 / 1',
      objectFit: 'cover',
    }),
    [isMobile],
  )

  return (
    <>
      <PageLayout gap={isMobile ? '2.5rem' : '6.25rem'} marginTop="0">
        <Hero
          title="CrowdBuilding voor gemeenten"
          subtitle="Alles wat er nodig is voor collectieve woonvormen in jouw gemeente op één plek."
          backgroundSrc={heroAboutUsImage}
          fullWidth
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: 'row',
          }}
          columns={[
            {
              markdown: `## Eenvoudig van woonbeleid naar uitvoering\n
Als expert in collectieve woonvormen is CrowdBuilding dé aangewezen partner voor gemeenten bij het faciliteren van collectieve zelfbouw.\n
Hoe informeer je inwoners en garandeer je dat initiatieven aan de juiste eisen voldoen? Hoe geef je kavels uit en begeleid je projecten efficiënt?\n
Met relevante kennis en een uniek platform helpt CrowdBuilding jouw gemeente bij het ondersteunen van inwoners in elke fase van hun woondroom, van oriëntatie tot realisatie.`,
              content: (
                <Button
                  variant="primary"
                  style={{ marginTop: '0.5rem' }}
                  label="Neem contact op"
                  onClick={() => (window.location.href = pages.contactUs)}
                />
              ),
              style: { alignItems: 'flex-start' },
            },
            isMobile
              ? null
              : {
                  imageSrc: transitionImage,
                  imageAlt: 'Eenvoudig van woonbeleid naar uitvoering',
                  imageStyle: {
                    ...imageStyle,
                    marginLeft: 'auto',
                  },
                },
          ]}
        />

        <div>
          <Title.H2
            style={{
              marginBottom: '1.75rem',
              marginTop: 0,
            }}
          >
            Onze partners
          </Title.H2>
          <OurPartners />
        </div>

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              imageSrc: starImage,
              imageAlt: 'tevreden klantillustratie',
              imageStyle: {
                ...imageStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `> "In onze samenwerking heeft Crowdbuilding ons idee tot leven gebracht. Met als resultaat een mooi platform waar Utrechtse wooncoöperaties terecht kunnen om zich in te schrijven voor een tender.
              
> Crowdbuilding gelooft in deze manier van wonen en dat merk je in de samenwerking en in hun enthousiasme. Ze doen wat ze zeggen, denken mee en zijn creatief en snel. Een aanrader wanneer je als gemeente deze stappen online wil ondernemen."\n
**Nikkie Gerritsen** - Beleidsadviseur Wonen voor de gemeente Utrecht`,
            },
          ]}
        />

        <div>
          <Title.H2 style={{ margin: '0 0 1rem 0' }}>
            Diensten voor gemeenten
          </Title.H2>
          <Paragraph.Medium style={{ maxWidth: '40rem' }}>
            CrowdBuilding is er om gemeenten te ontzorgen bij het faciliteren
            van collectieve zelfbouw als onderdeel van het woonbeleid. Via een
            op maat gemaakte gemeentepagina op het platform faciliteren we het
            volledige proces op overzichtelijke wijze. Van inventarisatie en
            registratie van geïnteresseerde groepen tot de selectie en uitgifte
            van kavels.
          </Paragraph.Medium>

          <ColumnsSection
            columns={[
              {
                content: (
                  <ServicesCard
                    title="Inventarisatie"
                    image={magnifyGlassImage}
                    content="Je wil als gemeente collectieve woonvormen stimuleren. Wie zijn de geïnteresseerde groepen? CrowdBuilding maakt de inventarisatie van groepen eenvoudig dankzij een combinatie van online en offline mogelijkheden."
                  />
                ),
              },
              {
                content: (
                  <ServicesCard
                    title="Registratie"
                    image={ideaBulbImage}
                    content="Het beleid is er, de interesse ook. En nu? Via een overzichtelijk en effectief registratieproces ondersteunt CrowdBuilding zowel de gemeente en collectieven bij het secuur vastleggen van alle benodigde gegevens."
                  />
                ),
              },
              {
                content: (
                  <ServicesCard
                    title="Selectie & Uitgifte"
                    image={creditCardImage}
                    content="Je hebt één of meerdere collectieven op het oog die de beoogde zelfbouwopgave zouden kunnen realiseren. Hoe maak je de uiteindelijke selectie en voer je deze zorgvuldig uit? Het CrowdBuilding-platform faciliteert drie verschillende methoden."
                  />
                ),
              },
            ]}
          />
        </div>

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              imageSrc: uniqueEnvironmentImage,
              imageAlt: 'unieke online omgeving',
              imageStyle: {
                ...imageStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `## Unieke online omgeving\n
Via de online omgeving van jouw gemeente op CrowdBuilding.com doorlopen geïnteresseerde groepen op overzichtelijke wijze alle benodigde stappen van oriëntatie tot realisatie.\n
Zo kan jouw team rekenen op aanmeldingen van goed georganiseerde wooninitiatieven met grote kans van slagen.`,
            },
          ]}
        />

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              markdown: `## Een betrouwbare kennispartner\n
De keuze om woningen te laten realiseren door een burgercollectief vraagt om een specifieke aanpak. In samenwerking met lokale kennispartners bieden we de meest relevante en recente informatie rondom het realiseren van collectieve woonvormen in jouw gemeente.
Meer weten? Neem vrijblijvend contact op.`,
              content: (
                <Button
                  variant="primary"
                  style={{ marginTop: '0.5rem' }}
                  label="Neem contact op"
                  onClick={() => (window.location.href = pages.contactUs)}
                />
              ),
              style: { alignItems: 'flex-start' },
            },
            {
              imageSrc: knowledgePartnerImage,
              imageAlt: 'betrouwbare kennispartner',
              imageStyle: {
                ...imageStyle,
                marginLeft: 'auto',
              },
            },
          ]}
        />

        <Column
          style={{
            textAlign: isMobile ? 'left' : 'center',
            alignItems: isMobile ? 'flex-start' : 'center',
          }}
        >
          <Title.H2 style={{ margin: 0 }}>Ervaar zelf hoe het werkt</Title.H2>
          <DemoTextComponent style={{ maxWidth: '40rem' }}>
            Benieuwd hoe CrowdBuilding jouw gemeente en haar inwoners
            ondersteunt bij het realiseren van collectieve woondromen? We nemen
            je graag mee door het platform en de mogelijkheden in een
            persoonlijk en vrijblijvend gesprek.
          </DemoTextComponent>
          <Button
            variant="primary"
            style={{ marginTop: '0.5rem' }}
            label="Demo aanvragen"
            onClick={() => (window.location.href = pages.contactUs)}
          />
        </Column>

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              markdown: `## Vragen?\n
Neem contact op om een telefonische afspraak in te plannen wanneer het jou uitkomt.`,
              style: { alignItems: 'flex-start' },
              content: (
                <Button
                  variant="primary"
                  style={{ marginTop: '0.5rem' }}
                  label="Neem contact op"
                  onClick={() => (window.location.href = pages.contactUs)}
                />
              ),
            },
            {
              content: (
                <div className="a a8"
                  style={{
                    gap: isMobile ? '0.5rem' : '1.5rem',}}
                >
                  <TeamCard
                    name="Sascha Glasl"
                    role="sascha@crowdbuilding.com"
                    image={saschaImage}
                  />
                  <TeamCard
                    name="Freddie Aldershoff"
                    role="freddie@crowdbuilding.com"
                    image={freddieImage}
                  />
                </div>
              ),
            },
          ]}
        />
      </PageLayout>
    </>
  )
}

interface ServicesCardProps {
  title: string
  image: string
  content: string
}

const ServicesCard = ({ title, image, content }: ServicesCardProps) => {
  return (
    <Card
      title={title}
      image={image}
      imageStyle={{
        height: 80,
        width: 80,
      }}
      padded
      border
      style={{ textAlign: 'left', flex: 1 }}
    >
      <Text.Medium>{content}</Text.Medium>
    </Card>
  )
}
